/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType>({ socket: null, isConnected: false });

export const useSocket = () => useContext(SocketContext);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  // Use ref to hold socket instance accessible in cleanup without adding to deps
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setSocket(null);
      setIsConnected(false);
      return;
    }

    const token = localStorage.getItem('erp_access_token');
    const apiUrlWithV1 =
      import.meta.env.VITE_API_URL ||
      (window.location.origin === 'http://localhost:5173' ? 'http://localhost:3000/api/v1' : '/api/v1');

    // Strip /api/v1 if present to get the base socket.io server URL
    const socketUrl = apiUrlWithV1.replace(/\/api\/v1\/?$/, '');

    let s: Socket | null = null;
    try {
      s = io(socketUrl || window.location.origin, {
        auth: { token },
        transports: ['websocket'],
        reconnectionAttempts: 5,
        reconnectionDelay: 5000,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[SocketProvider] CRITICAL: Socket initialization crashed:', msg);
      setSocket(null);
      setIsConnected(false);
      return;
    }

    socketRef.current = s;

    s.on('connect', () => {
      setIsConnected(true);
    });

    s.on('disconnect', (_reason: string) => {
      setIsConnected(false);
    });

    s.on('connect_error', (err: Error) => {
      console.error('[SocketProvider] Connection error:', err.message);
    });

    setSocket(s);

    return () => {
      s?.disconnect();
      socketRef.current = null;
    };
  }, [user]); // reconnect when user object changes (login/logout)

  const value = React.useMemo(() => ({ socket, isConnected }), [socket, isConnected]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};
